"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import { InstagramChat } from "./instagram-chat"
import { LinkedInChat } from "./linkedin-chat"
import { PlatformTabs } from "./platform-tabs"
import { FloatingChatHero } from "@/components/marketing/agent/floating-chat-hero"
import { useTranslations } from "next-intl"
import { capturePH } from "@/instrumentation-client"
import ReCAPTCHA from "react-google-recaptcha"
import { createPortal } from "react-dom"

interface Message {
  id: string
  text: string
  author: "me" | "contact"
  source: "website"
  sending: boolean
  sent_at: string
  attachments: any[]
  isTyping?: boolean
}

export default function ChatHero() {
  const tSeeds = useTranslations('chat.seeds')
  const tError = useTranslations('chat.error')
  const [activeTab, setActiveTab] = useState<"instagram" | "linkedin">("linkedin")
  const [instagramInput, setInstagramInput] = useState("")
  const [linkedinInput, setLinkedinInput] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const messageTimeoutsRef = useRef<NodeJS.Timeout[]>([])
  const mainChatRef = useRef<HTMLDivElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isMainChatVisible, setIsMainChatVisible] = useState(true)
  const [isFloatingOpen, setIsFloatingOpen] = useState(false)
  const [lastReadAtInstagram, setLastReadAtInstagram] = useState<number>(() => 0)
  const [lastReadAtLinkedin, setLastReadAtLinkedin] = useState<number>(() => 0)
  const captchaRef = useRef<ReCAPTCHA | null>(null)
  const [captchaSize, setCaptchaSize] = useState<"invisible" | "normal">("normal")
  const [showCaptchaOverlay, setShowCaptchaOverlay] = useState(false)
  const captchaResolveRef = useRef<((token?: string) => void) | null>(null)
  const [captchaTokenState, setCaptchaTokenState] = useState<string | undefined>(undefined)
  const [captchaChallengeOpen, setCaptchaChallengeOpen] = useState(false)
  const [showCaptchaBackdrop, setShowCaptchaBackdrop] = useState(false)

  // Generate initial timestamps (a few seconds ago)
  const getInitialTimestamp = (secondsAgo: number) => {
    const date = new Date()
    date.setSeconds(date.getSeconds() - secondsAgo)
    return date.toISOString()
  }



  // Cleanup on unmount
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onFocusIn = (e: FocusEvent) => {
      try {
        const target = e.target as HTMLElement | null
        if (!target) return
        if (target.tagName === 'IFRAME') {
          const iframe = target as HTMLIFrameElement
          const src = iframe.getAttribute('src') || ''
          const title = iframe.getAttribute('title') || ''
          if (/recaptcha/i.test(src) || /recaptcha/i.test(title)) {
            // On iOS, avoid toggling overlay visibility too fast; only hide backdrop
            // and keep overlay mounted so anchor stays alive
            setTimeout(() => setShowCaptchaBackdrop(false), 0)
          }
        }
      } catch {}
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => {
      // Allow external trigger (floating button) to request captcha before opening chat
      ensureCaptchaToken().then((tkn) => {
        try {
          if (tkn) document.body.setAttribute('data-recaptcha-token', '1')
        } catch {}
      })
    }
    window.addEventListener('theos:request-captcha', handler as EventListener)
    return () => window.removeEventListener('theos:request-captcha', handler as EventListener)
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (messageTimeoutsRef.current.length > 0) {
        messageTimeoutsRef.current.forEach(timeout => clearTimeout(timeout))
        messageTimeoutsRef.current = []
      }
    }
  }, [])

  // Track viewport width to determine mobile vs desktop
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 767px)')
    const setMatches = () => setIsMobile(mql.matches)
    setMatches()
    mql.addEventListener?.('change', setMatches)
    return () => mql.removeEventListener?.('change', setMatches)
  }, [])

  // Detect when the reCAPTCHA challenge is open (v2 checkbox) to avoid covering it
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!showCaptchaOverlay || captchaSize !== 'normal') return
    const check = () => {
      try {
        const frames = Array.from(document.querySelectorAll('iframe')) as HTMLIFrameElement[]
        const open = frames.some((f) => {
          const src = (f.getAttribute('src') || '').toLowerCase()
          const title = (f.getAttribute('title') || '').toLowerCase()
          const isChallengeIframe = (src.includes('/recaptcha/') && (src.includes('/bframe') || src.includes('fallback'))) || title.includes('recaptcha challenge')
          if (!isChallengeIframe) return false
          // Visible and large enough to be the puzzle, not the small anchor
          const rect = f.getBoundingClientRect()
          const largeEnough = rect.width > 200 && rect.height > 200
          const onScreen = rect.bottom > 0 && rect.right > 0 && rect.top < (window.innerHeight || 0) && rect.left < (window.innerWidth || 0)
          return largeEnough && onScreen
        })
        setCaptchaChallengeOpen(open)
        try {
          if (open) document.body.setAttribute('data-recaptcha-open', '1')
          else document.body.removeAttribute('data-recaptcha-open')
        } catch {}
        if (open) {
          // Hide only our backdrop so puzzle is unobstructed; keep widget mounted
          setShowCaptchaBackdrop(false)
        }
      } catch {
        setCaptchaChallengeOpen(false)
      }
    }
    const mo = new MutationObserver(() => check())
    mo.observe(document.body, { childList: true, subtree: true })
    // run once
    check()
    return () => {
      mo.disconnect()
      try { document.body.removeAttribute('data-recaptcha-open') } catch {}
    }
  }, [showCaptchaOverlay, captchaSize])

  // Observe main chat visibility on desktop to toggle floating chat
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!mainChatRef.current) return
    if (isMobile) return

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first) setIsMainChatVisible(first.isIntersecting)
    }, { threshold: 0.25 })

    observer.observe(mainChatRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  // Mark messages as read only when the floating chat is open
  useEffect(() => {
    if (!isFloatingOpen) return
    const now = Date.now()
    if (activeTab === "instagram") {
      setLastReadAtInstagram(now)
    } else {
      setLastReadAtLinkedin(now)
    }
  }, [isFloatingOpen, activeTab])

  const parseTime = (iso: string) => {
    try { return new Date(iso).getTime() } catch { return 0 }
  }

  const [instagramMessages, setInstagramMessages] = useState<Message[]>([
    {
      id: 'mid.ig-1',
      text: tSeeds('instagram.1'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(4),
      attachments: []
    },
    {
      id: 'mid.ig-2',
      text: tSeeds('instagram.2'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(3),
      attachments: []
    },
    {
      id: 'mid.ig-3',
      text: tSeeds('instagram.3'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(2),
      attachments: []
    },
    {
      id: 'mid.ig-4',
      text: tSeeds('instagram.4'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(1),
      attachments: []
    }
  ])
  const [linkedinMessages, setLinkedinMessages] = useState<Message[]>([
    {
      id: 'mid.lk-1',
      text: tSeeds('linkedin.1'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(4),
      attachments: []
    },
    {
      id: 'mid.lk-2',
      text: tSeeds('linkedin.2'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(3),
      attachments: []
    },
    {
      id: 'mid.lk-3',
      text: tSeeds('linkedin.3'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(2),
      attachments: []
    },
    {
      id: 'mid.lk-4',
      text: tSeeds('linkedin.4'),
      author: "me",
      source: "website",
      sending: false,
      sent_at: getInitialTimestamp(1),
      attachments: []
    }
  ])




  const unreadCountInstagram = instagramMessages.filter(m => m.author === 'me' && !m.isTyping && parseTime(m.sent_at) > lastReadAtInstagram).length
  const unreadCountLinkedin = linkedinMessages.filter(m => m.author === 'me' && !m.isTyping && parseTime(m.sent_at) > lastReadAtLinkedin).length

  // Acquire a reCAPTCHA token. Shows overlay for v2 checkbox and resolves when solved.
  const ensureCaptchaToken = async (): Promise<string | undefined> => {
    if (!process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) return undefined
    // If we already have a token (first message solved), reuse it
    if (captchaTokenState) return captchaTokenState
    // Try invisible first if enabled
    if (captchaSize === "invisible" && captchaRef.current) {
      try {
        const t = await captchaRef.current.executeAsync()
        if (t) setCaptchaTokenState(t)
        try { captchaRef.current.reset() } catch {}
        return t || undefined
      } catch (err) {
        console.warn("Invisible CAPTCHA failed, falling back to visible mode", err)
        setCaptchaSize("normal")
        setShowCaptchaOverlay(true)
        setShowCaptchaBackdrop(true)
        return new Promise<string | undefined>((resolve) => { captchaResolveRef.current = resolve })
      }
    }
    // Visible v2 checkbox: show overlay and wait for user interaction
    setShowCaptchaOverlay(true)
    setShowCaptchaBackdrop(true)
    return new Promise<string | undefined>((resolve) => { captchaResolveRef.current = resolve })
  }
  const callBackendAPI = async (messages: Message[], platform: "instagram" | "linkedin", captchaTokenOverride?: string) => {
    // Abort any existing request FIRST
    if (abortControllerRef.current) {
      console.log("Aborting previous request...")
      abortControllerRef.current.abort()
      abortControllerRef.current = null // Clear reference immediately
    }

    // Clear any pending message timeouts (from delayed message additions)
    if (messageTimeoutsRef.current.length > 0) {
      console.log(`Clearing ${messageTimeoutsRef.current.length} pending message timeouts...`)
      messageTimeoutsRef.current.forEach(timeout => clearTimeout(timeout))
      messageTimeoutsRef.current = []
    }

    // Remove any existing typing indicators
    if (platform === "instagram") {
      setInstagramMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping)
        console.log(`Removed ${prev.length - filtered.length} typing indicators`)
        return filtered
      })
    } else {
      setLinkedinMessages(prev => {
        const filtered = prev.filter(msg => !msg.isTyping)
        console.log(`Removed ${prev.length - filtered.length} typing indicators`)
        return filtered
      })
    }

    // Convert messages to API format
    const apiMessages = messages.map(msg => ({
      author: msg.author,
      text: msg.text,
      sent_at: msg.sent_at
    }))

    console.log("Calling API with messages:", apiMessages)

    // Add typing indicator immediately at the bottom of the chat
    const aiMessageId = `mid.${platform === 'instagram' ? 'ig' : 'lk'}-${Date.now()}-typing`
    console.log("Creating AI typing indicator with ID:", aiMessageId)
    const typingMessage: Message = {
      id: aiMessageId,
      text: "",
      author: "me",
      source: "website",
      sending: false,
      sent_at: new Date().toISOString(),
      attachments: [],
      isTyping: true
    }

    // Add typing indicator at the end of the messages array (will appear at bottom)
    if (platform === "instagram") {
      setInstagramMessages(prev => {
        console.log("Adding typing indicator at position:", prev.length)
        return [...prev, typingMessage]
      })
    } else {
      setLinkedinMessages(prev => {
        console.log("Adding typing indicator at position:", prev.length)
        return [...prev, typingMessage]
      })
    }

    // Declare aiMessageText outside try block so it's accessible in catch
    let aiMessageText = ""

    try {
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController()
      const abortSignal = abortControllerRef.current ? abortControllerRef.current.signal : undefined

      // You can change this to match your backend URL
      const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"
      const apiUrl = `${API_BASE_URL}/api/v1/conversations/landing/respond`
      console.log("API URL:", apiUrl)
      console.log("Note: Make sure your backend server is running on port 8000")

      // Ensure we have a captcha token (prefer provided one to avoid double prompts)
      let tokenForRequest = typeof captchaTokenOverride !== 'undefined' ? captchaTokenOverride : await ensureCaptchaToken()

      const doPost = async (captcha: string | undefined) => fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
        },
        body: JSON.stringify({ captcha: captcha || "", messages: apiMessages }),
        signal: abortSignal
      })

      let response = await doPost(tokenForRequest)

      console.log("Response status:", response.status)
      console.log("Response headers:", response.headers)

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Error response body:", errorText)
        // If backend indicates captcha invalid, re-acquire once and retry
        if (response.status >= 400 && response.status < 500 && /captcha/i.test(errorText)) {
          setCaptchaTokenState(undefined)
          tokenForRequest = await ensureCaptchaToken()
          if (tokenForRequest) setCaptchaTokenState(tokenForRequest)
          response = await doPost(tokenForRequest)
          if (!response.ok) {
            const retryText = await response.text()
            throw new Error(`HTTP error! status: ${response.status}, body: ${retryText}`)
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`)
        }
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error("No reader available")

      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('d:')) {
            // Decision event - just log it, don't stop generation
            try {
              const decisionData = line.slice(2)
              const parsed = JSON.parse(decisionData)
              console.log("Decision received:", parsed)
            } catch (e) {
              console.error("Error parsing decision:", e)
            }
          } else if (line.startsWith('0:')) {
            // Text chunk
            try {
              const textData = line.slice(2)
              const parsed = JSON.parse(textData)

              // Skip system messages
              if (parsed.includes('[System:')) {
                console.log("Skipping system message:", parsed)
                continue
              }

              aiMessageText += parsed

              // Keep typing indicator without showing the accumulated text
              // (we'll split and show messages at the end)
            } catch (e) {
              console.error("Error parsing text chunk:", e)
            }
          } else if (line.startsWith('e:')) {
            // End event - split message by newlines and add each as separate message
            const finalText = aiMessageText.trim()

            // Split by double newlines or single newlines  
            // Double newlines indicate stronger separation
            const messageParts = finalText
              .split(/\n+/)  // Split by any newlines
              .map(part => part.trim())
              .filter(part => part.length > 0)  // Remove empty parts

            console.log("Splitting message into parts:", messageParts)

            // Track branch generation if any part contains a Loom link
            try {
              const hasLoom = messageParts.some(p => /loom\.com/i.test(p))
              if (hasLoom) {
                capturePH('chat_branch_generated', {
                  platform,
                  provider: 'loom',
                })
              }
            } catch { /* no-op */ }

            // Remove the typing indicator
            if (platform === "instagram") {
              setInstagramMessages(prev => prev.filter(msg => msg.id !== aiMessageId))
            } else {
              setLinkedinMessages(prev => prev.filter(msg => msg.id !== aiMessageId))
            }

            // Add each message part with a delay
            messageParts.forEach((part, index) => {
              const timeout = setTimeout(() => {
                const newMessage: Message = {
                  id: `mid.${platform === 'instagram' ? 'ig' : 'lk'}-${Date.now()}-${index}`,
                  text: part,
                  author: "me",
                  source: "website",
                  sending: false,
                  sent_at: new Date().toISOString(),
                  attachments: [],
                  isTyping: false
                }

                if (platform === "instagram") {
                  setInstagramMessages(prev => [...prev, newMessage])
                } else {
                  setLinkedinMessages(prev => [...prev, newMessage])
                }

                // Remove this timeout from the ref after it's executed
                messageTimeoutsRef.current = messageTimeoutsRef.current.filter(t => t !== timeout)
              }, index * 500) // 500ms delay between messages

              // Store the timeout so it can be cancelled if needed
              messageTimeoutsRef.current.push(timeout)
            })
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request was aborted - cleaning up...')

        // Clear any pending timeouts that might have been set
        if (messageTimeoutsRef.current.length > 0) {
          console.log(`Clearing ${messageTimeoutsRef.current.length} pending timeouts from aborted request`)
          messageTimeoutsRef.current.forEach(timeout => clearTimeout(timeout))
          messageTimeoutsRef.current = []
        }

        // Remove the typing indicator - we don't want to show partial messages when aborted
        if (platform === "instagram") {
          setInstagramMessages(prev => prev.filter(msg => msg.id !== aiMessageId))
        } else {
          setLinkedinMessages(prev => prev.filter(msg => msg.id !== aiMessageId))
        }
      } else {
        console.error("Error calling backend API:", error)

        // Replace typing indicator with error message
        const fallbackMessage: Message = {
          id: aiMessageId,
          text: tError('generic'),
          author: "me",
          source: "website",
          sending: false,
          sent_at: new Date().toISOString(),
          attachments: [],
          isTyping: false
        }

        // Track generation failure
        try {
          capturePH('chat_message_generation_failed', {
            platform,
            error_name: error?.name,
          })
        } catch { /* no-op */ }

        if (platform === "instagram") {
          // Replace typing indicator with fallback message
          setInstagramMessages(prev =>
            prev.map(msg => msg.id === aiMessageId ? fallbackMessage : msg)
          )
        } else {
          // Replace typing indicator with fallback message
          setLinkedinMessages(prev =>
            prev.map(msg => msg.id === aiMessageId ? fallbackMessage : msg)
          )
        }
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleInstagramSend = async () => {
    if (instagramInput.trim()) {
      // Require CAPTCHA before sending any message
      const token = await ensureCaptchaToken()
      if (!token && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
        // CAPTCHA is required but not solved; don't proceed
        return
      }
      setShowCaptchaOverlay(false)
      // Filter out any typing indicators before adding new message
      const cleanMessages = instagramMessages.filter(msg => !msg.isTyping)
      const userMessageId = `mid.ig-user-${Date.now()}`
      const newMessage: Message = {
        id: userMessageId,
        text: instagramInput,
        author: "contact",
        source: "website",
        sending: false,
        sent_at: new Date().toISOString(),
        attachments: []
      }
      console.log("Creating user message with ID:", userMessageId)
      const updatedMessages = [...cleanMessages, newMessage]
      setInstagramMessages(updatedMessages)
      try {
        capturePH('chat_message_sent', {
          platform: 'instagram',
          message_id: userMessageId,
          message_length: instagramInput.trim().length,
          contains_url: /https?:\/\/|www\./i.test(instagramInput)
        })
      } catch { /* no-op */ }
      setInstagramInput("")
      setIsGenerating(true)
      await callBackendAPI(updatedMessages, "instagram", token)
    }
  }

  const handleLinkedinSend = async () => {
    if (linkedinInput.trim()) {
      // Require CAPTCHA before sending any message
      const token = await ensureCaptchaToken()
      if (!token && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
        // CAPTCHA is required but not solved; don't proceed
        return
      }
      setShowCaptchaOverlay(false)
      // Filter out any typing indicators before adding new message
      const cleanMessages = linkedinMessages.filter(msg => !msg.isTyping)
      const userMessageId = `mid.lk-user-${Date.now()}`
      const newMessage: Message = {
        id: userMessageId,
        text: linkedinInput,
        author: "contact",
        source: "website",
        sending: false,
        sent_at: new Date().toISOString(),
        attachments: []
      }
      console.log("Creating user message with ID:", userMessageId)
      const updatedMessages = [...cleanMessages, newMessage]
      setLinkedinMessages(updatedMessages)
      try {
        capturePH('chat_message_sent', {
          platform: 'linkedin',
          message_id: userMessageId,
          message_length: linkedinInput.trim().length,
          contains_url: /https?:\/\/|www\./i.test(linkedinInput)
        })
      } catch { /* no-op */ }
      setLinkedinInput("")
      setIsGenerating(true)
      await callBackendAPI(updatedMessages, "linkedin", token)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (messageTimeoutsRef.current.length > 0) {
        messageTimeoutsRef.current.forEach(timeout => clearTimeout(timeout))
        messageTimeoutsRef.current = []
      }
    }
  }, [])

  return (
    <>
      {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && captchaSize === "normal" && showCaptchaOverlay && typeof document !== 'undefined' && createPortal(
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 pointer-events-none" style={{ zIndex: captchaChallengeOpen ? 0 : 2147483647 }}>
          <div className="pointer-events-auto" style={captchaChallengeOpen ? { pointerEvents: 'none' } : undefined}>
            <ReCAPTCHA
              key="recaptcha-visible"
              ref={captchaRef}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              onChange={(token) => {
                const tkn = (token as string | null) || undefined
                if (!tkn) return
                setCaptchaTokenState(tkn)
                try { captchaResolveRef.current?.(tkn) } finally { captchaResolveRef.current = null }
                try { document.body.setAttribute('data-recaptcha-token', '1') } catch {}
                try { window.dispatchEvent(new CustomEvent('theos:captcha-solved')) } catch {}
                setTimeout(() => { setShowCaptchaOverlay(false); setShowCaptchaBackdrop(false) }, 0)
              }}
              onExpired={() => {
                setCaptchaTokenState(undefined)
              }}
              onErrored={() => {
                try { captchaRef.current?.reset() } catch {}
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && captchaSize === "normal" && showCaptchaBackdrop && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 bg-black/60" style={{ zIndex: captchaChallengeOpen ? 0 : 2147483646, pointerEvents: 'none' }} />,
        document.body
      )}
      <FloatingChatHero
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        instagramMessages={instagramMessages}
        setInstagramMessages={setInstagramMessages}
        linkedinMessages={linkedinMessages}
        setLinkedinMessages={setLinkedinMessages}
        instagramInput={instagramInput}
        setInstagramInput={setInstagramInput}
        linkedinInput={linkedinInput}
        setLinkedinInput={setLinkedinInput}
        handleInstagramSend={handleInstagramSend}
        handleLinkedinSend={handleLinkedinSend}
        visible={isMobile ? true : !isMainChatVisible}
        open={isFloatingOpen}
        onOpenChange={setIsFloatingOpen}
        unreadCountInstagram={unreadCountInstagram}
        unreadCountLinkedin={unreadCountLinkedin}
      />


      <div ref={mainChatRef} className="pointer-events-auto relative mx-auto h-auto w-full transform-gpu px-6  will-change-transform md:px-7 lg:px-8.5  mt-14 md:mt-20 lg:mt-22 hidden md:block">

        <div className="absolute -translate-y-14 w-full lg:-translate-x-8.5 -translate-x-6 flex justify-center z-10">
          <PlatformTabs value={activeTab} onTabChange={(p) => setActiveTab(p)} />
        </div>
        <div className="relative z-200 flex transform-gpu will-change-transform h-[500px] pointer-events-none" />
        <div className="relative z-200 flex transform-gpu will-change-transform pointer-events-none  ">
        </div>
        <div className="absolute top-0 left-0 z-10 flex h-[500px] w-full flex-col px-6 md:px-7 lg:h-18 lg:px-8.5">
          <div className="absolute inset-0 h-[500px] size-full rounded-[20px] bg-[#F2F4F9] shadow-[15px_15px_39.3px_0px_rgba(178,194,221,1),10px_20px_20px_0px_rgba(121,140,182,0.2),10px_4px_15.4px_0px_rgba(133,145,172,0.2),-10px_-4px_11.3px_0px_rgba(238,241,247,0.4),-18px_-9px_32.9px_0px_rgba(255,255,255,0.5),2px_2px_1.4px_0px_rgba(255,255,255,0.56)_inset]">
            <div className="absolute inset-0 overflow-hidden rounded-[20px] pointer-events-auto">
              <div className="relative w-full h-[500px]">
                {activeTab === "instagram" ? (
                  <InstagramChat
                    messages={instagramMessages}
                    input={instagramInput}
                    setInput={setInstagramInput}
                    onSend={handleInstagramSend}
                  />
                ) : (
                  <LinkedInChat
                    messages={linkedinMessages}
                    input={linkedinInput}
                    setInput={setLinkedinInput}
                    onSend={handleLinkedinSend}
                  />
                )}
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[20px] border-18 border-[#C4DBFF] opacity-15 blur-[10px]">
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[20px] border-8 border-white blur-xs">
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[20px] border-14 border-[#4FCDFF] opacity-10 blur-[14px]">
              </div>
            </div>
          </div>
        </div>
        <div className="hero-video-mask transform-gpu pointer-events-none absolute top-7.5 -left-1/2 z-0 aspect-[2468/1500] h-auto w-[201.042%] -translate-y-full will-change-transform lg:-left-72 lg:w-308.5  xl:block ">
          <Image src="/light-lamp.png" alt="" width={2468} height={1500} sizes="100vw" priority fetchPriority="high" className='absolute bottom-0 left-3 h-full w-full z-100 opacity-80 transition-opacity duration-200' />
        </div>

      </div>
    </>
  )
}

interface ChatProps {
  messages: Message[]
  input: string
  setInput: (value: string) => void
  onSend: () => void
}

